import { useContext, useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useQuery } from '@tanstack/react-query';

import { Text, Box, Flex, FormControl, FormLabel } from '@chakra-ui/react';

import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';

import FormInput from 'components/fields/auto/FormInput';
import DateInput from 'components/fields/auto/DateInput';
import RadioInput from 'components/fields/auto/RadioInput';
import UnlistedDrivers from 'views/auto_form/components/UnlistedDrivers';
import UnlistedSpouses from 'views/auto_form/components/UnlistedSpouses';
import Divider from 'views/auto_form/components/Divider';
import Message from 'components/fields/auto/Message';

import Driver from './components/Driver';

import { SECTIONS } from 'views/auto_form/constants';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import useIsBinderDisabled from 'views/auto_form/hooks/useIsBinderDisabled';

import getDateString from 'helpers/getDateString';

import { apiGetCarriers } from 'api';

import type { APICarrierData } from 'types/api.types';

import {
  changeDriverSummary,
  changeMostRecentPolicy,
} from 'reducers/autoPersonalFormReducer';
import {
  FILE_TYPE,
  PROVINCE_ALBERTA,
  isAtlanticProvince,
  isNonOntarioProvince,
} from 'variables/constants';

const Drivers = () => {
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const dispatch = useAppDispatch();

  const drivers = useAppSelector(
    (state) => state.autoPersonalForm.data.drivers,
  );

  const { data: carriersData = [], isLoading: isLoadingCarriers } = useQuery<
    APICarrierData[]
  >({
    queryKey: ['carriers'],
    queryFn: () => apiGetCarriers(1, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const vehicleCount = useAppSelector(
    (state) => state.autoPersonalForm.data.vehicles.length,
  );

  const driversSummary = useAppSelector(
    (state) => state.autoPersonalForm.data.drivers_summary,
  );

  const mostRecentPolicy = useAppSelector(
    (state) => state.autoPersonalForm.data.most_recent_policy,
  );

  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isNonOntario = isNonOntarioProvince(province);
  const isAtlantic = isAtlanticProvince(province);
  const isAlberta = province === PROVINCE_ALBERTA;

  const isBinderDisabled = useIsBinderDisabled();

  const validation026 = getValidationMessage(validations, '026');
  const validation022 = getValidationMessage(validations, '022');
  const validation027 = getValidationMessage(validations, '027');
  const validation029 = getValidationMessage(validations, '029');
  const validation072 = getValidationMessage(validations, '072');

  // Cancelation ones
  const validation028 = getValidationMessage(validations, '028');
  const validation120 = getValidationMessage(validations, '120');
  const validation126 = getValidationMessage(validations, '126');

  useEffect(() => {
    if (validation026) {
      dispatch(
        changeDriverSummary({
          name: 'is_other_persons_licensed_to_drive',
          value: true,
        }),
      );
    }

    if (validation027) {
      dispatch(
        changeDriverSummary({
          name: 'is_suspended_or_cancelled_last_6_years',
          value: true,
        }),
      );
    }

    if (validation028 || validation120 || validation126) {
      dispatch(
        changeDriverSummary({
          name: 'is_cancelled_last_3_years',
          value: true,
        }),
      );
    }

    if (validation029) {
      dispatch(
        changeDriverSummary({
          name: 'is_misrepresentation_last_3_years',
          value: true,
        }),
      );
    }
  }, [
    dispatch,
    validation026,
    validation027,
    validation028,
    validation029,
    validation120,
    validation126,
  ]);

  const onDriversSummaryChange = (event: any) => {
    dispatch(
      changeDriverSummary({
        name: event.target.name,
        value: event.target.checked,
      }),
    );
  };

  const onDriversSummaryTextChange = (event: any) => {
    dispatch(
      changeDriverSummary({
        name: event.target.name,
        value: event.target.value,
      }),
    );
  };

  const onMostRecentPolicyChange = (event: any) => {
    dispatch(
      changeMostRecentPolicy({
        name: event.target.name,
        value: event.target.value,
      }),
    );
  };

  const onMostRecentPolicyDateChange = (date: Date, name: string) => {
    dispatch(
      changeMostRecentPolicy({
        name,
        value: getDateString(date),
      }),
    );
  };

  const getCancelationRemarkValidationId = () => {
    if (validation028) return '028';
    if (validation120) return '120';
    if (validation126) return '126';
    return 'cancellations_last_3_years';
  };

  const getCancelationValidationMessage = () => {
    if (validation028) return validation028;
    if (validation120) return validation120;
    if (validation126) return validation126;
    return 'Please provide details';
  };

  return (
    <Card mb="20px" id="sectionDrivers">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Driver Information
      </Text>
      {drivers.map((driver, index: number) => (
        <Driver
          key={index}
          driver={driver}
          index={index}
          vehicle_count={vehicleCount}
        />
      ))}

      {validation022 && <UnlistedSpouses />}

      {!isAtlantic && (
        <>
          <Divider>Unlisted drivers</Divider>

          <Box mt="20px" mb="20px">
            <RadioInput
              section={SECTIONS.DRIVERS}
              label="Are any other persons in the household or business licensed to drive?"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(
                driversSummary.is_other_persons_licensed_to_drive,
              )}
              onChange={(value) =>
                onDriversSummaryChange({
                  target: {
                    name: 'is_other_persons_licensed_to_drive',
                    checked: value === 'true',
                  },
                })
              }
              message={{
                validationId: '026',
                hideMessage: !!validation026,
                hideRemarks: !!validation026,
                manual: {
                  message: 'Please provide details',
                  show:
                    !validation026 &&
                    driversSummary.is_other_persons_licensed_to_drive,
                },
              }}
              required
              disabled={!!validation026}
            />

            {validation026 && <UnlistedDrivers />}
          </Box>
        </>
      )}

      <Divider>
        What are the details of the applicant's most recent automobile
        insurance?
      </Divider>

      <Flex gap="20px" mt="20px">
        <Box w="30%">
          <FormControl>
            <FormLabel ml="0">Insurance Company</FormLabel>

            <SelectSearch
              name="company_name"
              value={mostRecentPolicy.company_name}
              onChange={(value) =>
                onMostRecentPolicyChange({
                  target: { name: 'company_name', value: value || '' },
                })
              }
              options={carriersData.map((carrier: APICarrierData) => ({
                value: carrier.name,
                label: carrier.name,
              }))}
              placeholder="Select..."
              isDisabled={isLoadingCarriers || isBinderDisabled}
              allowClear
              allowManualOther
            />
          </FormControl>
        </Box>

        <Box w="20%">
          <FormInput
            section={SECTIONS.DRIVERS}
            label="Policy No."
            name="policy_no"
            value={mostRecentPolicy.policy_no}
            onChange={(e) => onMostRecentPolicyChange(e)}
          />
        </Box>

        <Box w="15%">
          <DateInput
            section={SECTIONS.DRIVERS}
            label="Expiry Date"
            date={getDateFromString(mostRecentPolicy.expiry_date)}
            onChange={(date: Date) =>
              onMostRecentPolicyDateChange(date, 'expiry_date')
            }
          />
        </Box>
      </Flex>

      <Divider mt="20px">To the applicant's knowledge...</Divider>

      <Box mt="20px">
        <RadioInput
          section={SECTIONS.DRIVERS}
          label={
            isAlberta
              ? 'Has any driver’s licence, vehicle permit, or similar authorization issued to the applicant or drivers listed been or continued to be suspended, cancelled, or lapsed?'
              : isAtlantic
                ? 'Has any driver’s licence, vehicle permit or similar authorization issued to the applicant or drivers listed, to the knowledge of the applicant, been or continued to be suspended, cancelled or lapsed within the SIX years preceding this application?'
                : "Has any driver's licence, vehicle permit etc, issued to the applicant or to any person in the household or business been suspended or cancelled in the last 6 years?"
          }
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            driversSummary.is_suspended_or_cancelled_last_6_years,
          )}
          onChange={(value) =>
            onDriversSummaryChange({
              target: {
                name: 'is_suspended_or_cancelled_last_6_years',
                checked: value === 'true',
              },
            })
          }
          message={{
            validationId: '027',
            manual: {
              message: 'Please provide details',
              show:
                driversSummary.is_suspended_or_cancelled_last_6_years &&
                !validation027,
            },
          }}
          disabled={!!validation027}
        />

        <RadioInput
          section={SECTIONS.DRIVERS}
          label={
            isAlberta
              ? 'Has any Insurer, cancelled, declined, or refused to renew or issue automobile insurance to the applicant within the last 3 years?'
              : isAtlantic
                ? 'Has any insurer, to the knowledge of the applicant, cancelled, declined or refused to renew or issue automobile insurance to the applicant or drivers within the THREE years preceding this application?'
                : 'Has any insurance company cancelled automobile insurance for the applicant or any listed driver in the last 3 years?'
          }
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(driversSummary.is_cancelled_last_3_years)}
          onChange={(value) =>
            onDriversSummaryChange({
              target: {
                name: 'is_cancelled_last_3_years',
                checked: value === 'true',
              },
            })
          }
          message={{
            validationId: getCancelationRemarkValidationId(),
            manual: {
              message: getCancelationValidationMessage(),
              show:
                driversSummary.is_cancelled_last_3_years &&
                (!validation028 || !validation120 || !validation126),
            },
            file: validation126
              ? FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO
              : undefined,
          }}
          mt="10px"
          disabled={!!validation028 || !!validation120 || !!validation126}
        />

        {isNonOntario && driversSummary.is_cancelled_last_3_years && (
          <Box mt="10px">
            <FormInput
              section={SECTIONS.DRIVERS}
              label="Name of insurer"
              name="cancelled_insurer_name"
              value={driversSummary.cancelled_insurer_name || ''}
              onChange={onDriversSummaryTextChange}
              required
            />
            <FormInput
              section={SECTIONS.DRIVERS}
              label="Policy number (if available)"
              name="cancelled_policy_number"
              value={driversSummary.cancelled_policy_number || ''}
              onChange={onDriversSummaryTextChange}
            />
            <FormInput
              section={SECTIONS.DRIVERS}
              label="Reason"
              name="cancelled_reason"
              value={driversSummary.cancelled_reason || ''}
              onChange={onDriversSummaryTextChange}
              required
            />
          </Box>
        )}

        {!isNonOntario && (
          <>
            <RadioInput
              section={SECTIONS.DRIVERS}
              label="During the last 3 years, has any automobile insurance policy issued to the applicant or any listed driver been cancelled or has any claim been denied for material misrepresentation?"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(
                driversSummary.is_misrepresentation_last_3_years,
              )}
              onChange={(value) =>
                onDriversSummaryChange({
                  target: {
                    name: 'is_misrepresentation_last_3_years',
                    checked: value === 'true',
                  },
                })
              }
              message={{
                validationId: '029',
                manual: {
                  message: 'Please provide details',
                  show:
                    driversSummary.is_misrepresentation_last_3_years &&
                    !validation029,
                },
              }}
              mt="10px"
              disabled={!!validation029}
            />

            <RadioInput
              section={SECTIONS.DRIVERS}
              label="Has the applicant or any listed driver been found by a court to have committed a fraud connected with automobile insurance?"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(driversSummary.is_fraud)}
              onChange={(value) =>
                onDriversSummaryChange({
                  target: {
                    name: 'is_fraud',
                    checked: value === 'true',
                  },
                })
              }
              message={{
                validationId: 'fraud',
                manual: {
                  message: 'Please provide details',
                  show: driversSummary.is_fraud,
                },
              }}
              mt="10px"
              required
            />
          </>
        )}

        {isAtlanticProvince(province) && (
          <>
            <RadioInput
              section={SECTIONS.DRIVERS}
              label="Is any driver subject to fainting spells, dizziness, or loss of consciousness?"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(
                driversSummary.is_subject_to_fainting_spells,
              )}
              onChange={(value) =>
                onDriversSummaryChange({
                  target: {
                    name: 'is_subject_to_fainting_spells',
                    checked: value === 'true',
                  },
                })
              }
              message={{
                validationId: 'fainting_spells',
                hideRemarks: true,
                manual: {
                  message: 'Referral to Market Underwriting required.',
                  show: driversSummary.is_subject_to_fainting_spells,
                },
                file: driversSummary.is_subject_to_fainting_spells
                  ? FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO
                  : undefined,
              }}
              mt="10px"
              required
            />

            <RadioInput
              section={SECTIONS.DRIVERS}
              label="Has any driver ever suffered from a heart disorder, epilepsy, diabetes, defective vision or hearing, or any other physical or mental disability which might affect the safe operation of a vehicle?"
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(
                driversSummary.is_suffered_from_medical_condition,
              )}
              onChange={(value) =>
                onDriversSummaryChange({
                  target: {
                    name: 'is_suffered_from_medical_condition',
                    checked: value === 'true',
                  },
                })
              }
              message={{
                validationId: 'medical_condition',
                hideRemarks: true,
                manual: {
                  message: 'Referral to Market Underwriting required.',
                  show: driversSummary.is_suffered_from_medical_condition,
                },
                file: driversSummary.is_suffered_from_medical_condition
                  ? FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO
                  : undefined,
              }}
              mt="10px"
              required
            />
          </>
        )}
      </Box>

      {validation072 && (
        <>
          <Divider mt="20px">
            Portage - Cancellation & non-renewal history
          </Divider>

          <Message
            section={SECTIONS.DRIVERS}
            validationId="072"
            file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
          />
        </>
      )}
    </Card>
  );
};

export default Drivers;
